// FX Rates screen — tenant admin manages the currency-conversion table
// used by reinsurance credit notes when a market's currency differs from
// the deal currency. Kept as its own screen (not nested in the deal
// detail) because rates are tenant-wide config, not deal-specific data.

import React, { useEffect, useState, useCallback } from 'react';
import axios from 'axios';
import { useHistory } from 'react-router-dom';
import AppLayout from '../../components/AppLayout';
import useConfirm from '../../components/ConfirmDialog/useConfirm';

const BASE_URL = process.env.REACT_APP_BASE_URL || '';

const C = {
  ink:      '#1e293b',
  muted:    '#64748b',
  border:   '#e2e8f0',
  soft:     '#f8fafc',
  burgundy: '#800020',
};

const S = {
  page:    { flex: 1, overflowY: 'auto', padding: '28px 40px', minHeight: 0 },
  h1:      { fontSize: 24, fontWeight: 700, color: C.ink, margin: '0 0 4px' },
  sub:     { color: C.muted, margin: '0 0 20px', fontSize: 13, maxWidth: 720 },
  crumbs:  { fontSize: 12, color: C.muted, marginBottom: 6 },
  crumbLink:{ color: C.muted, textDecoration: 'none', cursor: 'pointer' },
  card:    { background: '#fff', border: `1px solid ${C.border}`, borderRadius: 12, padding: 22, marginBottom: 16 },
  section: { fontSize: 12, fontWeight: 700, color: C.burgundy, textTransform: 'uppercase', letterSpacing: 1.2, marginBottom: 14 },
  btn:     { padding: '9px 16px', borderRadius: 10, border: 'none', background: C.burgundy, color: '#fff', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  ghost:   { padding: '9px 16px', borderRadius: 10, border: `1px solid ${C.border}`, background: '#fff', color: '#334155', cursor: 'pointer', fontWeight: 600, fontSize: 13 },
  danger:  { padding: '4px 10px', borderRadius: 6, border: '1px solid #fca5a5', background: '#fff', color: '#b91c1c', cursor: 'pointer', fontWeight: 600, fontSize: 12 },
  input:   { padding: '9px 11px', borderRadius: 8, border: `1px solid ${C.border}`, fontSize: 13, background: '#fff', color: C.ink, fontFamily: 'inherit', width: '100%', boxSizing: 'border-box' },
  label:   { fontSize: 11, fontWeight: 600, color: C.muted, textTransform: 'uppercase', letterSpacing: 0.6, marginBottom: 4, display: 'block' },
  formRow: { display: 'grid', gridTemplateColumns: '80px 80px 1fr 140px 140px auto', gap: 10, alignItems: 'end' },
  table:   { width: '100%', borderCollapse: 'collapse', fontSize: 13, marginTop: 8 },
  th:      { textAlign: 'left', padding: '10px 12px', background: C.soft, color: C.muted, fontWeight: 600, fontSize: 11, textTransform: 'uppercase', letterSpacing: 0.5, borderBottom: `1px solid ${C.border}` },
  td:      { padding: '10px 12px', borderBottom: '1px solid #f1f5f9', color: C.ink },
  err:  { color: '#dc2626', fontSize: 13, marginTop: 8 },
  ok:   { color: '#166534', fontSize: 13, marginTop: 8 },
};

const emptyForm = () => ({
  base: '', quote: '', rate: '', effectiveDate: new Date().toISOString().slice(0, 10), source: 'manual', notes: '',
});

const fmtDate = (v) => {
  if (!v) return '—';
  try { return new Date(v).toISOString().slice(0, 10); }
  catch (e) { return String(v).slice(0, 10); }
};

const FXRates = () => {
  const history = useHistory();
  const { confirm, ConfirmPortal } = useConfirm();
  const [rows, setRows]       = useState([]);
  const [loading, setLoading] = useState(true);
  const [form, setForm]       = useState(emptyForm());
  const [saving, setSaving]   = useState(false);
  const [error, setError]     = useState(null);
  const [ok, setOk]           = useState(null);

  const fetchRates = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const resp = await axios.get(`${BASE_URL}/reinsurance/fx-rates`, { withCredentials: true });
      setRows(resp?.data?.data || []);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to load FX rates');
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { fetchRates(); }, [fetchRates]);

  const onField = (k) => (e) => setForm((p) => ({ ...p, [k]: e.target.value }));

  const upsert = async () => {
    setError(null); setOk(null);
    const base = String(form.base || '').toUpperCase().trim();
    const quote = String(form.quote || '').toUpperCase().trim();
    const rate = Number(form.rate);
    if (!base || !quote || base.length !== 3 || quote.length !== 3) { setError('base + quote must be 3-letter ISO codes (USD, QAR, EUR, …)'); return; }
    if (base === quote) { setError('base and quote must differ'); return; }
    if (!Number.isFinite(rate) || rate <= 0) { setError('rate must be positive'); return; }
    setSaving(true);
    try {
      await axios.post(`${BASE_URL}/reinsurance/fx-rates`, {
        base, quote, rate,
        effectiveDate: form.effectiveDate || undefined,
        source: form.source || 'manual',
        notes:  form.notes  || undefined,
      }, { withCredentials: true });
      setOk(`Saved 1 ${base} = ${rate} ${quote}`);
      setForm({ ...emptyForm(), effectiveDate: form.effectiveDate });
      await fetchRates();
      setTimeout(() => setOk(null), 2000);
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to save rate');
    } finally {
      setSaving(false);
    }
  };

  const remove = async (id) => {
    const ok = await confirm({
      title: 'Delete FX rate?',
      message: 'This FX rate row will be permanently removed.',
      confirmText: 'Delete',
      variant: 'danger',
    });
    if (!ok) return;
    try {
      await axios.delete(`${BASE_URL}/reinsurance/fx-rates/${id}`, { withCredentials: true });
      await fetchRates();
    } catch (err) {
      setError(err?.response?.data?.message || err.message || 'Failed to delete');
    }
  };

  return (
    <AppLayout>
      <div style={S.page}>
        <div style={S.crumbs}>
          <span style={S.crumbLink} onClick={() => history.push('/reinsurance/deals')}>Reinsurance Deals</span>
          <span> / FX Rates</span>
        </div>
        <h1 style={S.h1}>Reinsurance FX Rates</h1>
        <p style={S.sub}>
          Tenant-managed currency conversion table used by credit notes when a reinsurer's currency differs from the deal currency.
          Rates are quoted as <b>1 base = rate × quote</b> (e.g. 1 USD = 3.64 QAR). One row per pair per effective date;
          re-saving the same pair on the same day updates the row. Credit notes pick the latest row on or before the deal's period start.
        </p>

        {/* ── Add / update rate ── */}
        <div style={S.card}>
          <div style={S.section}>Add or update a rate</div>
          <div style={S.formRow}>
            <div>
              <label style={S.label}>Base</label>
              <input style={S.input} placeholder='USD' value={form.base} onChange={onField('base')} maxLength={3} />
            </div>
            <div>
              <label style={S.label}>Quote</label>
              <input style={S.input} placeholder='QAR' value={form.quote} onChange={onField('quote')} maxLength={3} />
            </div>
            <div>
              <label style={S.label}>Rate (1 base = ? quote)</label>
              <input style={S.input} type='number' step='0.000001' placeholder='3.64' value={form.rate} onChange={onField('rate')} />
            </div>
            <div>
              <label style={S.label}>Effective date</label>
              <input style={S.input} type='date' value={form.effectiveDate} onChange={onField('effectiveDate')} />
            </div>
            <div>
              <label style={S.label}>Source</label>
              <input style={S.input} placeholder='manual' value={form.source} onChange={onField('source')} />
            </div>
            <div style={{ alignSelf: 'end' }}>
              <button style={saving ? { ...S.btn, opacity: 0.6, cursor: 'wait' } : S.btn} onClick={upsert} disabled={saving}>
                {saving ? 'Saving…' : 'Save rate'}
              </button>
            </div>
          </div>
          <div style={{ marginTop: 10 }}>
            <label style={S.label}>Notes (optional)</label>
            <input style={S.input} value={form.notes} onChange={onField('notes')} placeholder='e.g. ECB reference rate, mid-market fix' />
          </div>
          {error && <div style={S.err}>{error}</div>}
          {ok    && <div style={S.ok}>{ok}</div>}
        </div>

        {/* ── Rates table ── */}
        <div style={S.card}>
          <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'baseline' }}>
            <div style={S.section}>Rates on file</div>
            <button style={S.ghost} onClick={fetchRates} disabled={loading}>{loading ? 'Loading…' : 'Refresh'}</button>
          </div>
          {rows.length === 0 && !loading ? (
            <div style={{ padding: 24, textAlign: 'center', color: C.muted, background: C.soft, borderRadius: 8, fontSize: 13 }}>
              No FX rates yet. Add one above — the reinsurance closings service will use it automatically.
            </div>
          ) : (
            <table style={S.table}>
              <thead>
                <tr>
                  <th style={S.th}>Pair</th>
                  <th style={S.th}>Rate (1 base = ? quote)</th>
                  <th style={S.th}>Effective</th>
                  <th style={S.th}>Source</th>
                  <th style={S.th}>Notes</th>
                  <th style={S.th}></th>
                </tr>
              </thead>
              <tbody>
                {rows.map((r) => (
                  <tr key={r._id}>
                    <td style={{ ...S.td, fontWeight: 600, color: C.burgundy }}>{r.base} / {r.quote}</td>
                    <td style={S.td}>{Number(r.rate).toLocaleString('en-US', { maximumFractionDigits: 6 })}</td>
                    <td style={S.td}>{fmtDate(r.effectiveDate)}</td>
                    <td style={S.td}>{r.source || '—'}</td>
                    <td style={S.td}>{r.notes || '—'}</td>
                    <td style={S.td}><button style={S.danger} onClick={() => remove(r._id)}>Delete</button></td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      </div>
      {ConfirmPortal}
    </AppLayout>
  );
};

export default FXRates;
